import { getSupabaseClient } from "@/services/supabaseClient"

export type CreateProfileInvitationInput = {
  recipientUserId: string
  recipientShareKey: string
  senderDisplayName: string
  recipientDisplayName: string
  invitationKind: "project" | "team"
  projectName: string
  roleTitle: string
  projectDescription: string
  timeline: string
  message: string
}

export type ProfileInvitation = {
  id: string
  status: "pending" | "accepted" | "declined"
  projectName: string
  roleTitle: string
}

function requireText(value: string, label: string) {
  const trimmed = value.trim()
  if (!trimmed) throw new Error(`${label} is required.`)
  return trimmed
}

export async function createProfileInvitation(input: CreateProfileInvitationInput) {
  const client = getSupabaseClient()
  if (!client) throw new Error("Supabase is not configured for this build.")

  const { data: userResult, error: userError } = await client.auth.getUser()
  if (userError) throw userError
  if (!userResult.user) throw new Error("Sign in before sending a Passport invitation.")

  const { data, error } = await client
    .from("profile_invitations")
    .insert({
      sender_user_id: userResult.user.id,
      recipient_user_id: input.recipientUserId,
      recipient_share_key: requireText(input.recipientShareKey, "Profile link"),
      sender_display_name: requireText(input.senderDisplayName, "Your display name"),
      recipient_display_name: requireText(input.recipientDisplayName, "Recipient"),
      invitation_kind: input.invitationKind,
      project_name: requireText(input.projectName, "Project or team name"),
      role_title: requireText(input.roleTitle, "Role"),
      project_description: requireText(input.projectDescription, "Project description"),
      timeline: requireText(input.timeline, "Timeline"),
      message: requireText(input.message, "Message"),
    })
    .select("id,status,project_name,role_title")
    .single()

  if (error) throw error

  return {
    id: String(data.id),
    status: data.status as ProfileInvitation["status"],
    projectName: String(data.project_name),
    roleTitle: String(data.role_title),
  } satisfies ProfileInvitation
}
